import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";
import { ApiError } from "./errors";

/**
 * Whose name is on an operator's internet invoices (owner OB-12, 2026-10-28;
 * radaccess v1.198, table from owner SQL 277).
 *
 * An operator without its own licence chooses its OWN name (needs its GSTIN,
 * or GST registration Unregistered: then no GSTIN and no GST, owner 2026-10-28)
 * or its UPSTREAM's name -- and the upstream's name needs the upstream's
 * recorded consent, because those invoices use the upstream's GSTIN and
 * series and count in its turnover (AGR).
 *
 *   GET  /access/operator/{ns}/invoicename                          this operator's card
 *   POST /access/operator/{ns}/invoicename/request                  ask the upstream
 *   POST /access/operator/{ns}/invoicename/own                      use its own name
 *   GET  /access/operator/{ns}/invoicename/requests                 requests made TO {ns}
 *   PUT  /access/operator/{ns}/invoicename/requests/{id}/accept     consent
 *   PUT  /access/operator/{ns}/invoicename/requests/{id}/decline    refuse (reason)
 *   PUT  /access/operator/{ns}/invoicename/requests/{id}/withdraw   take consent back (reason)
 *
 * 🔑 Owner and Admin ask and consent; Billing and superadmin read (RBAC "invoicename").
 */

const enc = (s: string) => encodeURIComponent(s);
const base = (ns: string) => `/operator/${enc(ns)}/invoicename`;

export type ConsentStatus = "requested" | "accepted" | "declined" | "cancelled" | "withdrawn";

export interface InvoiceNameConsent {
  id: number;
  namespace: string;
  upstreamNamespace: string;
  /** Legal names on record (radaccess v1.207+); absent when none is. */
  namespaceLegalName?: string;
  upstreamLegalName?: string;
  status: ConsentStatus;
  consentTextVersion: string;
  requestedBy: string;
  requestedAt: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
}

export interface InvoiceNameState {
  namespace: string;
  hasLicenceRecord: boolean;
  holdsLicence: boolean;
  identity: "own" | "branch_of_upstream";
  hasGstin: boolean;
  /** GST registration UNREGISTERED (radaccess v1.200+): own name, no GSTIN, no GST. */
  unregistered?: boolean;
  /**
   * radbilling accepts it as the supplier of its own internet (licensed today,
   * accepted Internet Distributor, or the override; radaccess v1.204+).
   * Absent from an older radaccess: treated as unknown, never as a refusal.
   */
  mayIssueOwn?: boolean;
  upstream?: string;
  legalName?: string;
  upstreamLegalName?: string;
  open?: InvoiceNameConsent;
  history: InvoiceNameConsent[];
}

/** The consent wording radaccess records as consentTextVersion "inc-v1". */
export const CONSENT_TEXT_VERSION = "inc-v1";
/**
 * How the screen names an operator: by its legal name only (owner 2026-10-29),
 * never by its namespace. Without one on record it says so.
 */
export function legalName(name: string | undefined, role = "the operator"): string {
  const n = (name ?? "").trim();
  return n || `${role} (no legal name on record yet: Edit profile)`;
}

export function consentText(operatorName: string): string {
  return (
    `We agree that the internet invoices of ${operatorName} are issued in our name, ` +
    "under our GSTIN and our invoice series, and count in our turnover (AGR). " +
    "We can withdraw this at any time; its invoices then return to its own name."
  );
}

export const STATUS_LABEL: Record<ConsentStatus, string> = {
  requested: "Waiting for an answer",
  accepted: "Agreed",
  declined: "Declined",
  cancelled: "Cancelled",
  withdrawn: "Withdrawn",
};

export const getInvoiceName = async (i: CallerIdentity, ns: string, signal?: AbortSignal) =>
  (await callGateway<InvoiceNameState>(i, config().gatewayBase, base(ns), signal ? { signal } : {})).data;

export const requestUpstreamName = async (i: CallerIdentity, ns: string) =>
  (await callGateway<InvoiceNameConsent>(i, config().gatewayBase, `${base(ns)}/request`, { method: "POST" })).data;

export const chooseOwnName = async (i: CallerIdentity, ns: string) =>
  (await callGateway<InvoiceNameState>(i, config().gatewayBase, `${base(ns)}/own`, { method: "POST" })).data;

export const listNameRequests = async (i: CallerIdentity, ns: string, signal?: AbortSignal) =>
  (await callGateway<InvoiceNameConsent[]>(i, config().gatewayBase, `${base(ns)}/requests`, signal ? { signal } : {})).data ?? [];

export const acceptNameRequest = async (i: CallerIdentity, ns: string, id: number) =>
  (await callGateway<InvoiceNameConsent>(i, config().gatewayBase, `${base(ns)}/requests/${id}/accept`, { method: "PUT", body: {} })).data;

export const declineNameRequest = async (i: CallerIdentity, ns: string, id: number, note: string) =>
  (await callGateway<InvoiceNameConsent>(i, config().gatewayBase, `${base(ns)}/requests/${id}/decline`, { method: "PUT", body: { note } })).data;

export const withdrawNameConsent = async (i: CallerIdentity, ns: string, id: number, note: string) =>
  (await callGateway<InvoiceNameConsent>(i, config().gatewayBase, `${base(ns)}/requests/${id}/withdraw`, { method: "PUT", body: { note } })).data;

/** radaccess's refusal, in words; its 409s already carry the sentence. */
export function invoiceNameRefusal(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 503) return "Choosing whose name is on invoices is not switched on yet.";
    if (e.status === 403) return "Refused: only the Owner or Admin decides whose name is on invoices.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Why its own name would stop its internet sales, or null. Shown in the
 * upstream's name (the switch is refused) and in its own name (sales are
 * already refused: testsite, 2026-10-28).
 */
export function ownNameBlocker(s: InvoiceNameState): string | null {
  if (!s.hasLicenceRecord || s.mayIssueOwn !== false) return null;
  const up = legalName(s.upstreamLegalName, "your upstream provider");
  return s.identity === "branch_of_upstream"
    ? "You cannot switch to your own name yet: invoicing internet in your own name needs a verified telecom licence " +
        "or accepted Internet Distributor responsibility. Until then your invoices stay in " + up + "'s name."
    : "Your internet sales are refused: invoicing internet in your own name needs a verified telecom licence or " +
        "accepted Internet Distributor responsibility. Ask " + up + " to invoice in its name.";
}

/** What the operator's card says about its internet invoices today. */
export function nameSummary(s: InvoiceNameState): string {
  if (!s.hasLicenceRecord) return "Not set yet: record whether you hold a telecom licence first (Edit profile › Licence).";
  if (s.identity === "branch_of_upstream") return `In ${legalName(s.upstreamLegalName, "your upstream provider")}'s name, GSTIN and invoice series.`;
  if (s.open?.status === "requested") return `In your own name, until ${legalName(s.open.upstreamLegalName, "your upstream provider")} agrees to your request.`;
  if (s.hasGstin) return "In your own name, under your GSTIN.";
  if (s.unregistered) return "In your own name. Not registered for GST: your invoices carry no GSTIN and no GST.";
  return "In your own name. Your GSTIN is not on record yet, or set GST registration to Unregistered (Edit profile › Tax).";
}
